import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useLang } from '../../../utils/i18n';

// Renders one content block within a footer column, based on its type.
const FooterBlock = ({ block, lang }) => {
  if (block.type === "image" && block.image?.url) {
    const img = (
      <img draggable="false" src={block.image.url} alt="" className="max-w-full rounded" style={{ width: `${block.width || 100}%` }} />
    );
    return block.link ? (
      <a href={block.link} target="_blank" rel="noreferrer" className="inline-block">{img}</a>
    ) : img;
  }

  if (block.type === "code" && block.code) {
    return <div dangerouslySetInnerHTML={{ __html: block.code }} />;
  }

  if (block.type === "text" && block.content) {
    const displayContent = lang === 'zh' && block.contentZh ? block.contentZh : block.content;
    return <div className="text-sm leading-relaxed text-gray-300" dangerouslySetInnerHTML={{ __html: displayContent }} />;
  }

  if (block.type === "links") {
    return (
      <div className="flex flex-col gap-1.5">
        {block.links?.map((item, i) => (
          <a href={item.url} target="_blank" rel="noreferrer" className="text-sm text-gray-300 hover:text-white hover:underline" key={i}>{lang === 'zh' && item.nameZh ? item.nameZh : item.name}</a>
        ))}
      </div>
    );
  }

  return null;
};

const Footer = () => {

  const location = useLocation();
  const { lang } = useLang();
  const [adminRoute, setAdminRoute] = useState(false);

  const [columns, setColumns] = useState([]);
  const [companyName, setCompanyName] = useState("1313 Mart");
  const [paymentNote, setPaymentNote] = useState("Cash on Delivery available");
  const [paymentNoteZh, setPaymentNoteZh] = useState("");
  const [utilityLinks, setUtilityLinks] = useState([]);

  useEffect(() => {
    setAdminRoute(location.pathname.split("/", 2).includes("admin"))
  }, [location]);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const { data } = await axios.get('/api/v1/footer-columns');
        setColumns(data.columns);
      } catch (error) {
        console.error('Failed to load footer columns', error);
      }
    };
    fetchColumns();

    const fetchSettings = async () => {
      try {
        const { data } = await axios.get('/api/v1/site-settings');
        if (data.settings.footerCompanyName) setCompanyName(data.settings.footerCompanyName);
        if (data.settings.footerPaymentNote) setPaymentNote(data.settings.footerPaymentNote);
        if (data.settings.footerPaymentNoteZh) setPaymentNoteZh(data.settings.footerPaymentNoteZh);
        setUtilityLinks(data.settings.footerUtilityLinks || []);
      } catch (error) {
        console.error('Failed to load footer settings', error);
      }
    };
    fetchSettings();
  }, []);

  const leftColumns = columns.filter((c) => c.side === "left");
  const rightColumns = columns.filter((c) => c.side === "right");

  const headingSizeClass = { sm: "text-xs", md: "text-sm", lg: "text-lg" };

  const renderColumn = (col) => (
    <div className="w-full sm:flex-1 flex flex-col gap-3 my-4 sm:my-6 px-5 sm:px-0 sm:ml-5" key={col._id}>
      {!col.hideHeading && (
        <h2 className={`text-primary-grey font-semibold uppercase tracking-wide ${headingSizeClass[col.headingSize] || "text-xs"}`}>{lang === 'zh' && col.headingZh ? col.headingZh : col.heading}</h2>
      )}
      <div className="flex flex-col gap-3">
        {col.blocks?.map((block, i) => (
          <FooterBlock key={i} block={block} lang={lang} />
        ))}
      </div>
    </div>
  );

  return (
    <>
      {!adminRoute && (
        <>
          <footer className="w-full py-2 sm:py-6 px-4 sm:px-12 bg-primary-darkBlue text-white border-b border-gray-600 flex flex-col sm:flex-row overflow-hidden">

            {leftColumns.length > 0 && (
              <div className="w-full sm:flex-[7] flex flex-col sm:flex-row">
                {leftColumns.map(renderColumn)}
              </div>
            )}

            {leftColumns.length > 0 && rightColumns.length > 0 && (
              <div className="border-gray-600 w-1 border-l mx-5 my-6 hidden sm:block"></div>
            )}

            {rightColumns.length > 0 && (
              <div className="w-full sm:flex-[5] flex flex-col sm:flex-row gap-2 sm:gap-6">
                {rightColumns.map(renderColumn)}
              </div>
            )}

          </footer>

          <div className="px-4 sm:px-16 py-4 sm:py-6 w-full bg-primary-darkBlue flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-white">
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2">
              {utilityLinks.map((link, i) => (
                <a href={link.url} target="_blank" rel="noreferrer" className="hover:underline" key={i}>{lang === 'zh' && link.labelZh ? link.labelZh : link.label}</a>
              ))}
            </div>

            <span className="text-gray-300">&copy; {new Date().getFullYear()} {companyName}</span>
            <span className="text-gray-300">{lang === 'zh' && paymentNoteZh ? paymentNoteZh : paymentNote}</span>
          </div>
        </>
      )}
    </>
  )
};

export default Footer;
